import React, { useMemo } from 'react';
import { computeSubtitleLayerTextLayout } from '@/lib/subtitles/subtitleEditorLayout';
import { normalizeRotationSigned } from '@/lib/subtitles/subtitleTransform';
import {
  subtitleAnimationClassName,
  subtitleTextContainerStyle,
} from '@/lib/subtitles/subtitleStyleConfig';
import { CaptionLines } from '@/lib/captions/captionLines';

/**
 * Single caption renderer for editor preview, export framing preview, and Chromium export parity.
 * Layout + typography come from the same helpers the export worker mirrors in HTML/CSS.
 */
export default function CaptionOverlay({
  text = '',
  lines: linesProp = null,
  tokens = null,
  highlightColor = '#fde047',
  style,
  layer,
  frameW,
  frameH,
  aspectRatio = '16:9',
  viewportMin = 900,
  /** When set, layout is computed at export resolution then scaled to frameW×frameH. */
  layoutFrameW = null,
  layoutFrameH = null,
  className = '',
  opacity = 1,
  placeholder = '',
  showGuide = false,
  guideClassName = 'border border-dashed border-violet-400/70 rounded-sm',
  testId = 'caption-overlay',
}) {
  const trimmed = String(text || '').trim();
  const layoutRefW = layoutFrameW ?? frameW;
  const layoutRefH = layoutFrameH ?? frameH;
  const layout = useMemo(
    () => computeSubtitleLayerTextLayout({
      text: trimmed || placeholder || 'Caption',
      layer,
      style,
      frameW: layoutRefW,
      frameH: layoutRefH,
      aspectRatio,
      viewportMin: layoutFrameW != null
        ? Math.max(layoutRefW, layoutRefH)
        : viewportMin,
    }),
    [trimmed, placeholder, layer, style, layoutRefW, layoutRefH, aspectRatio, layoutFrameW, viewportMin],
  );

  const displayText = trimmed || (placeholder ? String(placeholder) : '');
  const lines = linesProp?.length ? linesProp : layout.lines;
  if (!displayText && !showGuide) return null;

  const rot = normalizeRotationSigned(layer?.rotation || 0);
  const animClass = subtitleAnimationClassName(style?.animation);
  const textContainerStyle = subtitleTextContainerStyle(style, layout, displayText);
  const scaleX = frameW / Math.max(1, layoutRefW);
  const scaleY = frameH / Math.max(1, layoutRefH);
  const rect = {
    left: layout.rect.left * scaleX,
    top: layout.rect.top * scaleY,
    width: layout.rect.width * scaleX,
    height: layout.rect.height * scaleY,
  };
  const fontPxExact = layout.fontPxExact * scaleY;

  return (
    <div
      className={`absolute inset-0 pointer-events-none ${className}`.trim()}
      data-testid={testId}
    >
      {showGuide ? (
        <div
          className={`absolute ${guideClassName}`}
          style={{
            left: rect.left,
            top: rect.top,
            width: rect.width,
            height: rect.height,
            transform: `rotate(${rot}deg)`,
            transformOrigin: 'center center',
          }}
          aria-hidden
        />
      ) : null}
      {displayText ? (
        <div
          className={`absolute flex flex-col justify-center px-1 py-0.5 ${animClass}`}
          style={{
            left: rect.left,
            top: rect.top,
            width: rect.width,
            height: rect.height,
            transform: `rotate(${rot}deg)`,
            transformOrigin: 'center center',
            opacity,
            WebkitFontSmoothing: 'antialiased',
            ...textContainerStyle,
            fontSize: `${fontPxExact}px`,
          }}
        >
          <CaptionLines
            lines={lines.length ? lines : [displayText]}
            tokens={tokens}
            highlightColor={highlightColor}
            animation={style?.animation}
            keyPrefix={testId}
          />
        </div>
      ) : null}
    </div>
  );
}
